import React, { useState, useEffect } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import { useForm } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
import Select from "react-select"
import moment from "moment"

import { createSearchFormSchema } from "./schema"
import styles from "./index.module.css"

import { useData } from "../../contexts/DataContext"
import Calendar from "../../components/Calendar"
import Loader from "../../components/Loader"

const DEFAULT_DEPARTURE = "JFK"
const DEFAULT_ARRIVAL = "TLV"
const DEFAULT_TRIPTYPE = "round-trip"

function Home() {
    const { inventory, airports, loading } = useData()

    const getInitialDates = (departure, arrival, passengerCount) => {
        const nextDeparture = inventory.find(
            (item) =>
                item.departure === departure &&
                item.arrival === arrival &&
                moment(item.departureDate).isAfter(moment()) &&
                item.availableTickets >= passengerCount
        )?.departureDate || null

        const nextReturn = inventory.find(
            (item) =>
                item.departure === arrival &&
                item.arrival === departure &&
                moment(item.departureDate).isAfter(moment(nextDeparture).add(7, "days")) &&
                item.availableTickets >= passengerCount
        )?.departureDate || null
        return { nextDeparture, nextReturn }
    }

    const navigate = useNavigate()
    const location = useLocation()
    const [showCalendar, setShowCalendar] = useState("")
    const queryParams = new URLSearchParams(location.search)
    const initialDeparture = queryParams.get("departure") || DEFAULT_DEPARTURE
    const initialArrival = queryParams.get("arrival") || DEFAULT_ARRIVAL
    const initialTripType = queryParams.get("tripType") || DEFAULT_TRIPTYPE

    // Only calculate initial dates if inventory is loaded
    const { nextDeparture: initialDepartureDate, nextReturn: initialReturnDate } =
        !loading ? getInitialDates(initialDeparture, initialArrival, 1) : { nextDeparture: null, nextReturn: null }

    const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm({
        resolver: yupResolver(createSearchFormSchema(inventory)),
        defaultValues: {
            departure: initialDeparture,
            arrival: initialArrival,
            tripType: initialTripType,
            departureDate: initialDepartureDate,
            returnDate: initialReturnDate,
            passengerCount: 1
        }
    })

    // Convert airports data to options format for Select
    const locationOptions = airports.map(airport => ({
        value: airport.code,
        label: `${airport.label} (${airport.code})`
    }))

    useEffect(() => {
        if (!loading && inventory.length) {
            setValue("departureDate", initialDepartureDate)
            setValue("returnDate", initialReturnDate)
        }
    }, [loading, inventory, initialDepartureDate, initialReturnDate, setValue])

    if (loading) {
        return <Loader />
    }

    const searchFormValues = watch()

    const handleSwapLocations = () => {
        let newArrival = searchFormValues.departure
        let newDeparture = searchFormValues.arrival
        let { nextDeparture: departureDate, nextReturn: returnDate } = getInitialDates(newArrival, newDeparture, searchFormValues.passengerCount)

        setValue("departure", newDeparture)
        setValue("arrival", newArrival)
        setValue("departureDate", departureDate)
        if (searchFormValues.tripType === "one-way") {
            setValue("returnDate", returnDate)
        }
    }

    const handleLocationChange = (locationType, location) => {
        setValue(locationType, location)
    }


    const handleDateSelect = (dateType, date) => {
        if (!date) return
        if (dateType === "departure") {
            setValue("departureDate", date)
        } else if (dateType === "return") {
            setValue("returnDate", date)
        }
    }

    const onSubmit = async (data) => {
        try {
            let paramsToPass = {
                departure: data.departure,
                arrival: data.arrival,
                passengerCount: data.passengerCount,
                promoCode: data.promoCode,
                departureDate: moment(data.departureDate).unix(),
            }
            if (data.tripType === "round-trip" && data.returnDate) {
                paramsToPass["returnDate"] = moment(data.returnDate).unix()
            }

            const queryParams = new URLSearchParams(paramsToPass).toString()
            navigate(`/flight-form?${queryParams}`)
        } catch (error) {
            console.error("Something went wrong:", error)
        }
    }

    const CustomSelect = (props) => (
        <Select
            {...props}
            isDisabled={props.isDisabled}
            value={props.options.find((option) => option.value === props.value)}
        />
    )

    return (
        <section className={styles.homeSectionWrapper}>
            <div className="row mb-4">
                <div className="col-12 text-center">
                    <h1>Purchase your Onward Israel 2026 Flight</h1>
                </div>
                <div className="col-8 offset-2 text-center">
                    <h6>
                        Taglit-Birthright Israel has saved special group flight availability for this summer for you to get to your Onward Israel Program if your 10-day or volunteer flight was cancelled/moved, or if your own Onward flight was on a non-Israeli carrier.
                        These flights are subsidized by Taglit-Birthright Israel, but you will be responsible for $1,000 of the round trip airfare.
                    </h6>
                </div>
            </div>
            <form onSubmit={handleSubmit(onSubmit)}>
                <div className="row my-4" >
                    <div className="col-3 form-group mt-4">
                        <label className={`${styles.tripTypeRadio} ${searchFormValues.tripType === "round-trip" ? styles.tripTypeRadioActive : ""}`}>
                            <input
                                type="radio"
                                name="tripType"
                                value="round-trip"
                                className="d-none"
                                {...register("tripType")}
                                checked={watch("tripType") === "round-trip"}
                            />
                            <span className="material-symbols-outlined me-1">repeat</span>Round Trip
                        </label>
                        <label className={`${styles.tripTypeRadio} ${watch("tripType") === "one-way" ? styles.tripTypeRadioActive : ""}`}>
                            <input
                                type="radio"
                                name="tripType"
                                value="one-way"
                                className="d-none"
                                {...register("tripType")}
                                checked={searchFormValues.tripType === "one-way"}
                            />
                            <span className="material-symbols-outlined me-1">arrow_forward</span>One Way
                        </label>
                    </div>
                </div>
                <div className="row">
                    <div className="col">
                        <div className={`form-group ${styles.ricoFormGroup}`}>
                            <label className={styles.label}>
                                <span className={styles.labelText}>From</span>
                                <span className={`material-symbols-outlined ${styles.labelIcon}`}>
                                    flight_takeoff
                                </span>
                            </label>
                            <CustomSelect
                                name="departure"
                                options={locationOptions}
                                defaultValue={locationOptions.find((option) => option.value === searchFormValues.departure)}
                                value={locationOptions.find((option) => option.value === searchFormValues.departure)}
                                onChange={(e) => handleLocationChange("departure", e.value)}
                            />
                        </div>
                    </div>
                    <div className={`col-1 ${styles.swapContainer}`}>
                        <div className={styles.swapItem} onClick={handleSwapLocations}>
                            <span className={`material-symbols-outlined ${styles.swapIcon}`}>
                                swap_horiz
                            </span>
                        </div>
                    </div>
                    <div className="col">
                        <div className={`form-group ${styles.ricoFormGroup}`}>
                            <label className={styles.label}>
                                <span className={styles.labelText}>To</span>
                                <span className={`material-symbols-outlined ${styles.labelIcon}`}>
                                    flight_land
                                </span>
                            </label>
                            <CustomSelect
                                name="arrival"
                                options={locationOptions}
                                defaultValue={locationOptions.find((option) => option.value === searchFormValues.arrival)}
                                value={locationOptions.find((option) => option.value === searchFormValues.arrival)}
                                onChange={(e) => handleLocationChange("arrival", e.value)}
                            />
                        </div>
                    </div>
                    <div className="col">
                        <div className={`form-group ${styles.ricoFormGroup}`}>
                            <label htmlFor="passengerCountInput" className={styles.label}>
                                <span className={styles.labelText}>No of Seats</span>
                                <span className={`material-symbols-outlined ${styles.labelIcon}`}>
                                    airline_seat_recline_extra
                                </span>
                            </label>
                            <input
                                type="number"
                                id="passengerCountInput"
                                min={1}
                                {...register("passengerCount")}
                                className={`form-control ${errors.passengerCount ? "is-invalid" : ""}`}
                            />
                            {errors.passengerCount && (
                                <div className="invalid-feedback">{errors.passengerCount.message}</div>
                            )}
                        </div>
                    </div>
                    <div className="col">
                        <div className={`form-group ${styles.ricoFormGroup}`}>
                            <label htmlFor="departureDateInput" className={styles.label}>
                                <span className={styles.labelText}>
                                    {searchFormValues.tripType === "one-way" ? "Journey Date" : "Departure Date"}
                                </span>
                                <span className={`material-symbols-outlined ${styles.labelIcon}`}>
                                    calendar_month
                                </span>
                            </label>
                            <input
                                type="text"
                                className={`form-control ${errors.departureDate ? "is-invalid" : ""}`}
                                id="departureDateInput"
                                value={searchFormValues.departureDate || ""}
                                onClick={() => {
                                    setShowCalendar("openDepartureCalender")
                                }}
                                readOnly
                            />
                            {errors.departureDate && (
                                <div className="invalid-feedback">{errors.departureDate.message}</div>
                            )}
                            {showCalendar === "openDepartureCalender" && (
                                <Calendar
                                    showCalendar={showCalendar === "openDepartureCalender"}
                                    departure={searchFormValues.departure}
                                    arrival={searchFormValues.arrival}
                                    passengerCount={searchFormValues.passengerCount}
                                    defaultValue={searchFormValues.departureDate}
                                    onDateSelect={(date) => handleDateSelect("departure", date)}
                                    onClose={() => setShowCalendar("")}
                                />
                            )}
                        </div>
                    </div>
                    {searchFormValues.tripType === "round-trip" && (
                        <div className="col">
                            <div className={`form-group ${styles.ricoFormGroup}`}>
                                <label htmlFor="returnDateInput" className={styles.label}>
                                    <span className={styles.labelText}>Return Date</span>
                                    <span className={`material-symbols-outlined ${styles.labelIcon}`}>
                                        calendar_month
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    className={`form-control ${errors.returnDate ? "is-invalid" : ""}`}
                                    id="returnDateInput"
                                    value={searchFormValues.returnDate || ""}
                                    onClick={() => { setShowCalendar("openReturnCalender") }}
                                    readOnly
                                />
                                {errors.returnDate && (
                                    <div className="invalid-feedback">{errors.returnDate.message}</div>
                                )}
                                {showCalendar === "openReturnCalender" && (
                                    <Calendar
                                        showCalendar={showCalendar === "openReturnCalender"}
                                        departure={searchFormValues.arrival}
                                        arrival={searchFormValues.departure}
                                        passengerCount={searchFormValues.passengerCount}
                                        defaultValue={searchFormValues.returnDate}
                                        onDateSelect={(date) => handleDateSelect("return", date)}
                                        onClose={() => setShowCalendar("")}
                                    />
                                )}
                            </div>
                        </div>
                    )}
                </div>
                <div className="row">
                    <div className={`offset-9 col-3 form-group mt-4 ${styles.promoCodeInputContainer}`} >
                        <input
                            type="text"
                            name="promoCode"
                            placeholder="Enter Promo Code"
                            className={`form-control ${errors.promoCode ? "is-invalid" : ""} ${styles.promoCodeInput}`}
                            {...register("promoCode")}
                        />
                        {errors.promoCode && (
                            <div className="invalid-feedback">{errors.promoCode.message}</div>
                        )}
                        <p className="text-primary" data-bs-toggle="modal" data-bs-target="#promoHelpModal" style={{ cursor: "pointer", textDecoration: "underline" }}>
                            Don't have the promo?
                        </p>
                        <div className="modal fade" id="promoHelpModal" tabIndex="-1" aria-labelledby="promoHelpModalLabel" aria-hidden="true">
                            <div className="modal-dialog">
                                <div className="modal-content">
                                    <div className="modal-header">
                                        <h5 className="modal-title" id="promoHelpModalLabel">Need a Promo Code?</h5>
                                        <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                                    </div>
                                    <div className="modal-body">
                                        <p>Please must reach out to your Federation or email us at <a href="mailto:reservations@tisaisrael.com" className="text-primary" style={{ textDecoration: "underline", marginLeft: "5px" }}>reservations@tisaisrael.com</a> in order to proceed with your booking!</p>
                                    </div>
                                    <div className="modal-footer">
                                        <button type="button" className="btn btn-primary" data-bs-dismiss="modal">Close</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className={styles.buttonContainer}>
                    {searchFormValues.passengerCount > 9 && <p className="d-block">
                        More then 9 passengers is considered a group please email us at<a href="mailto:Groups@TisaIsrael.com" className="text-primary" style={{ textDecoration: "underline", marginLeft: "5px" }}>groups@tisaisrael.com</a> to share details and get custom quote.
                    </p>}
                    <button type="submit" className="btn-primary" disabled={searchFormValues.passengerCount > 9} >Search</button>
                </div>
                <div className="row">
                    <div className="col-12">
                        <small>
                            <b>Disclaimer:</b>
                            {/* <br />Birthright Israel is not purchasing these flights for you and the purchase of this flight is not directly connected to Birthright or your Trip Organizer. However, in an effort to make getting back to Israel for these last minute programs easier, Tisa Travel is coordinating these flights on Arkia Airlines. All flights include 1 checked baggage, a personal item and a carry on bag. */}
                            <b> You can choose your own arrival and departure dates that fit your schedule, but please ensure you are in Israel for the entirety of your program. </b>
                        </small>
                    </div>
                </div>
            </form>
        </section>
    )
}

export default Home